import React, { useCallback, useEffect, useState } from 'react';
import {
    ChevronDown, ChevronRight, Mail, MessageSquare, MousePointerClick,
    MailOpen, Send, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';

import {
    fetchPropertySends, fetchSendRecipients,
} from '../../../features/communications/api/communicationsApi';

/**
 * What has been bulk emailed/texted to THIS listing's people, and who opened it.
 *
 * WHY (Paul Jimoulis, CRM-2026-00072, 21 Sep 2026): "when i bulk email or text
 * buyers who have inspected or enquired on a property, there is no
 * communication section to see the info of how many people opened the
 * email/text. You can only see the texts and emails you send to the database
 * tabs not the proeprty buyers."
 *
 * The EMAIL / TEXT buttons on the Enquiries, Attendees and Buyers tabs reuse
 * the Find Buyers composer, so every send was filed as a generic "Find Buyers"
 * blast with no listing on it. The send now carries its property, and this is
 * the panel that reads it back - on the listing, where the agent is standing
 * when they want to know whether the email landed.
 */

const fmt = (n) => Number(n || 0).toLocaleString();

const when = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? ''
        : d.toLocaleString('en-AU', {
            day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
        });
};

/** One number above the table. */
function Stat({ icon: Icon, label, value, sub }) {
    return (
        <div className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                <Icon size={12} className="text-[#55C5D0]" />
                {label}
            </p>
            <p className="mt-1 text-xl font-bold text-[#374151]">{value}</p>
            {sub && <p className="text-[11px] text-gray-400">{sub}</p>}
        </div>
    );
}

/**
 * Who one send went to. Loaded only when the row is opened: a listing can carry
 * a dozen sends of a few hundred people each, and nobody reads them all.
 */
function Recipients({ sendId, channel }) {
    const isEmail = channel !== 'sms';
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let live = true;
        fetchSendRecipients(sendId)
            .then((data) => { if (live) setRows(Array.isArray(data) ? data : []); })
            .catch(() => { if (live) setError(true); });
        return () => { live = false; };
    }, [sendId]);

    if (error) return <p className="px-4 py-3 text-xs text-red-500">Couldn't load who this went to.</p>;
    if (rows === null) return <p className="px-4 py-3 text-xs text-gray-400">Loading recipients…</p>;
    if (!rows.length) return <p className="px-4 py-3 text-xs text-gray-400">No recipients recorded.</p>;

    return (
        <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <tr>
                        <th className="px-4 py-2 font-bold">Who</th>
                        <th className="px-4 py-2 font-bold">Sent</th>
                        {isEmail && <th className="px-4 py-2 font-bold">Opened</th>}
                        {isEmail && <th className="px-4 py-2 font-bold">Clicked</th>}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => (
                        <tr key={r.id} className="text-gray-700">
                            <td className="px-4 py-2">
                                <span className="font-semibold text-[#374151]">
                                    {r.contact_name || r.person_email || r.person_phone || '—'}
                                </span>
                                {(r.contact_name && (r.person_email || r.person_phone)) && (
                                    <span className="block text-[11px] text-gray-400">
                                        {r.person_email || r.person_phone}
                                    </span>
                                )}
                            </td>
                            <td className="px-4 py-2 text-gray-500">{when(r.occurred_at) || r.status}</td>
                            {isEmail && (
                                <td className="px-4 py-2">
                                    {r.opened_at
                                        ? <span className="font-semibold text-emerald-600">{when(r.opened_at)}</span>
                                        : <span className="text-gray-300">—</span>}
                                </td>
                            )}
                            {isEmail && (
                                <td className="px-4 py-2">
                                    {r.clicked_at
                                        ? <span className="font-semibold text-emerald-600">{when(r.clicked_at)}</span>
                                        : <span className="text-gray-300">—</span>}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function PropertyCommunications({ propertyId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(() => {
        if (!propertyId) return;
        setLoading(true);
        fetchPropertySends(propertyId)
            .then(setData)
            .catch(() => toast.error("Couldn't load this listing's communications"))
            .finally(() => setLoading(false));
    }, [propertyId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <p className="text-sm text-gray-400">Loading communications…</p>;

    const sends = data?.sends || [];
    const totals = data?.totals || {};

    if (!sends.length) {
        return (
            <p className="text-sm text-gray-500">
                Nothing has been bulk emailed or texted from this listing yet. Use the
                EMAIL or TEXT buttons on the Enquiries, Attendees or Buyers tabs and the
                send will show up here with its open and click rates.
            </p>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <Stat icon={Send} label="Sends" value={fmt(totals.sends)} />
                <Stat
                    icon={Users}
                    label="Messages out"
                    value={fmt(totals.went_out)}
                    sub={`${fmt(totals.recipients)} on the lists`}
                />
                <Stat
                    icon={MailOpen}
                    label="Opened"
                    value={fmt(totals.opened)}
                    sub={`${totals.open_rate ?? 0}% of the ${fmt(totals.email_went_out)} emails`}
                />
                <Stat
                    icon={MousePointerClick}
                    label="Clicked"
                    value={fmt(totals.clicked)}
                    sub={`${totals.click_rate ?? 0}% of the ${fmt(totals.email_went_out)} emails`}
                />
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200">
                {sends.map((s) => {
                    const open = openId === s.id;
                    const Icon = s.channel === 'sms' ? MessageSquare : Mail;
                    return (
                        <div key={s.id} className="border-b border-gray-100 last:border-b-0">
                            <button
                                type="button"
                                onClick={() => setOpenId(open ? null : s.id)}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                            >
                                {open
                                    ? <ChevronDown size={14} className="shrink-0 text-gray-400" />
                                    : <ChevronRight size={14} className="shrink-0 text-gray-400" />}
                                <Icon size={14} className="shrink-0 text-[#55C5D0]" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-semibold text-[#374151]">
                                        {s.name}
                                    </span>
                                    <span className="block truncate text-[11px] text-gray-400">
                                        {s.source_label}
                                        {s.created_by_name ? ` · ${s.created_by_name}` : ''}
                                        {s.automatic ? ' · Automatic' : ''}
                                        {s.sent_at ? ` · ${when(s.sent_at)}` : ''}
                                        {s.status === 'sending' ? ' · still sending' : ''}
                                    </span>
                                </span>
                                <span className="hidden shrink-0 gap-6 text-right sm:flex">
                                    <span>
                                        <span className="block text-[10px] uppercase tracking-wider text-gray-400">Out</span>
                                        <span className="text-sm font-bold text-[#374151]">
                                            {fmt(s.sent_count + s.delivered_count)}
                                        </span>
                                    </span>
                                    {/* A text has no open or click we can tie back to
                                        the send, so it says so rather than printing a
                                        0 that reads as "nobody opened it". */}
                                    {s.channel === 'sms' ? (
                                        <span className="self-center text-[11px] text-gray-400">
                                            Opens and clicks not tracked for texts
                                        </span>
                                    ) : (
                                        <>
                                            <span>
                                                <span className="block text-[10px] uppercase tracking-wider text-gray-400">Opened</span>
                                                <span className="text-sm font-bold text-[#374151]">
                                                    {fmt(s.opened_count)}
                                                    <span className="ml-1 text-[11px] font-normal text-gray-400">
                                                        {s.open_rate}%
                                                    </span>
                                                </span>
                                            </span>
                                            <span>
                                                <span className="block text-[10px] uppercase tracking-wider text-gray-400">Clicked</span>
                                                <span className="text-sm font-bold text-[#374151]">
                                                    {fmt(s.clicked_count)}
                                                    <span className="ml-1 text-[11px] font-normal text-gray-400">
                                                        {s.click_rate}%
                                                    </span>
                                                </span>
                                            </span>
                                        </>
                                    )}
                                </span>
                            </button>
                            {open && (
                                <div className="border-t border-gray-100 bg-gray-50/50">
                                    {s.message_text && (
                                        <p className="whitespace-pre-wrap border-b border-gray-100 px-4 py-3 text-xs text-gray-600">
                                            {s.message_text.slice(0, 600)}
                                            {s.message_text.length > 600 ? '…' : ''}
                                        </p>
                                    )}
                                    <Recipients sendId={s.id} channel={s.channel} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Said out loud, because an agent who sent something last week and
                finds it missing here would otherwise read this as still broken. */}
            <p className="text-[11px] text-gray-400">
                Opens and clicks are email only. A text tells us it was delivered, but a
                tap on a link inside it cannot be traced back to one send, so texts show
                how many went out and nothing more. Bulk emails and texts sent from this
                listing before 21 Sept 2026 were not tagged with the property and stay in
                the Communications Hub only.
            </p>
        </div>
    );
}
